/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  getFileTypeIconUrl,
  normalizeValueArrayObjectToString,
  normalizeValueArrayToString,
  setPreviewUrl
} from '@/utils/items'
import { licensedAssetsUrls } from '@/configs/urls'
import { createId } from '@/utils/general'
import { formatEmptyValue } from '@/utils/assets'
import { getBatchNames, getDefaultCardDetailsData } from '@/utils/cards.utils'
import { ASSET_LICENSING_BATCH_LABEL } from '@/configs/strings/items-labels'

const useAssetListing = ({
  onPurchaseLicense
}) => {
  const getCommonValues = (item) => {
    const title = item.title
    const assetId = item.assetId
    const creators = formatEmptyValue(
      normalizeValueArrayObjectToString(item.creators, 'name')
    )
    const isLicensed = item.licensedAsset !== null
    const buttonLabel = isLicensed ? 'Manage' : 'License'
    const onAction = !isLicensed && onPurchaseLicense &&
      (() => onPurchaseLicense(item.id))
    const publishers = formatEmptyValue(
      normalizeValueArrayObjectToString(item.publishers, 'name')
    )
    const fileType = formatEmptyValue(
      normalizeValueArrayToString(item.fileType)
    )
    const assetLicensingBatch = getBatchNames({
      containerValue: item.licensedAssets,
      batchKey: 'assetsLicensesBatch',
      batchNameKey: 'name'
    })
    const url = item.licensedAsset
      ? `/${licensedAssetsUrls.base}/${item.licensedAsset.id}`
      : ''
    const classification = item.type
    const organization = item.organization?.name

    return {
      assetId,
      assetLicensingBatch,
      buttonLabel,
      classification,
      creators,
      fileType,
      isLicensed,
      onAction,
      organization,
      publishers,
      title,
      url
    }
  }

  const getCardProps = (item) => {
    const {
      assetId,
      assetLicensingBatch,
      buttonLabel,
      classification,
      creators,
      fileType,
      isLicensed,
      onAction,
      publishers,
      title,
      url
    } = getCommonValues(item)
    const earned = isLicensed ? [{ value: '$ 0 earned' }] : []
    const details = [
      ...getDefaultCardDetailsData({
        publisherValue: publishers,
        fileTypeValue: fileType,
        assetIdValue: assetId
      }),
      {
        label: ASSET_LICENSING_BATCH_LABEL,
        value: assetLicensingBatch
      },
      ...earned
    ]

    return {
      buttonLabel,
      classification,
      creators,
      details,
      imageUrl: setPreviewUrl(item.coverUrl, fileType),
      isLink: item.licensedAsset !== null,
      key: createId(),
      onAction,
      title,
      url
    }
  }

  const getCardPropByTable = (item) => {
    const {
      assetId,
      assetLicensingBatch,
      buttonLabel,
      classification,
      creators,
      fileType,
      isLicensed,
      onAction,
      organization,
      publishers,
      title,
      url
    } = getCommonValues(item)
    const earned = isLicensed ? '$ 0' : 'N/A'

    return {
      assetId,
      assetLicensingBatch,
      buttonLabel,
      classification,
      creators,
      earned,
      fileType,
      iconUrl: getFileTypeIconUrl(fileType),
      isLicensed,
      key: createId(),
      onAction,
      organization,
      publishers,
      title,
      url
    }
  }

  const tableColumns = [
    {
      accessorKey: 'icon',
      enableHiding: false,
      header: ''
    },
    {
      accessorKey: 'assetId',
      enableHiding: false,
      header: 'Asset Id'
    },
    {
      accessorKey: 'title',
      enableHiding: false,
      header: 'Title'
    },
    {
      accessorKey: 'earned',
      enableHiding: false,
      header: 'Earned'
    },
    {
      header: 'Creators',
      accessorKey: 'creators'
    },
    {
      header: 'Publishers',
      accessorKey: 'publishers'
    },
    {
      header: 'File Type',
      accessorKey: 'fileType'
    },
    {
      header: 'Asset Licensing Batch',
      accessorKey: 'assetLicensingBatch'
    },
    {
      header: 'Organization',
      accessorKey: 'organization'
    },
    {
      accessorKey: 'actions',
      enableHiding: false,
      header: ''
    }
  ]

  const tableInitialState = {
    columnVisibility: {}
  }

  const defaultHiddenColumns = [
    'creators',
    'publishers',
    'fileType',
    'assetLicensingBatch',
    'organization'
  ]

  defaultHiddenColumns.forEach(
    (id) => (tableInitialState.columnVisibility[id] = false)
  )

  return {
    getCardPropByTable,
    getCardProps,
    tableInitialState,
    tableColumns
  }
}

export default useAssetListing
